import { useEffect, useRef, useState } from "react";
import { Sparkles, Workflow } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

// Roadmap 3.4 — sugerencia de categoría en el panel "Clasificar movimiento".
// Componente AISLADO a propósito: la estructura del panel de Conciliación no
// se toca (restricción de compatibilidad con la rama responsive); este bloque
// solo añade una línea con la sugerencia y preselecciona la categoría si el
// usuario aún no eligió nada. La fuente puede ser una regla manual del tenant
// (manda siempre) o la IA (solo con consentimiento; sin él, el endpoint
// responde vacío y aquí no se pinta nada). Clasificar sigue siendo decisión
// humana: la sugerencia solo preselecciona, nunca contabiliza.

export const confidenceLabel = (c) =>
  c >= 0.8 ? "alta" : c >= 0.6 ? "media" : "baja";

export default function ClassifySuggestion({
  transaction,
  categories,
  accounts,
  hasSelection,
  onApply,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const appliedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchSuggestion = async () => {
      try {
        const res = await apiFetch(`${API}/reconciliation/classify-suggestions`, {
          method: "POST",
          body: JSON.stringify({ transaction_ids: [transaction.transaction_id] }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const s = data.suggestions?.[transaction.transaction_id];
        if (s && !cancelled) setSuggestion(s);
      } catch {
        // Sin sugerencia no pasa nada: la clasificación manual sigue intacta.
      }
    };
    fetchSuggestion();
    return () => {
      cancelled = true;
    };
  }, [transaction.transaction_id]);

  useEffect(() => {
    if (!suggestion || appliedRef.current || hasSelection) return;
    appliedRef.current = true;
    onApply(suggestion);
  }, [suggestion, hasSelection, onApply]);

  if (!suggestion) return null;

  const category = categories.find((c) => c.key === suggestion.category_key);
  const account = accounts.find((a) => a.code === suggestion.account_code);
  const label =
    category?.label ||
    (account ? `${account.code} - ${account.name}` : suggestion.account_code);
  const isAi = suggestion.source === "ai";

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1.5 min-w-0"
      data-testid="classify-suggestion"
    >
      {isAi ? (
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <Workflow className="w-3.5 h-3.5 shrink-0" />
      )}
      <span className="font-medium shrink-0">
        {isAi ? "Sugerencia IA:" : "Regla aplicada:"}
      </span>
      <span className="truncate min-w-0" title={suggestion.reason}>
        {label}
        {isAi && suggestion.confidence != null
          ? ` (confianza ${confidenceLabel(suggestion.confidence)})`
          : ""}
        {suggestion.reason ? ` — ${suggestion.reason}` : ""}
      </span>
    </div>
  );
}
